import { t as translate } from 'i18next';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useIntegrations } from '@/hooks/useIntegrations';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import { IntercomIntegration } from '@/shared/types/integration';
import { createYupResolver } from '@/util/forms/createYupResolver';
import { CLEAR_DIRTY_VALUES, KEEP_DIRTY_VALUES } from '@/util/forms/resetOptions';
import { welcomeTriggerMessageValidator } from '@/util/validation/rules/integration-rules';

interface Props {
  integration: IntercomIntegration;
  canWrite: boolean;
}

type WelcomeTriggerForm = { welcome_trigger_message: string };

const FORM_ID = 'intercom-welcome-trigger-form';
const { maxLength } = welcomeTriggerMessageValidator;

const schema = yup.object({
  welcome_trigger_message: yup
    .string()
    .trim()
    .max(maxLength, () => translate('validation.max_length', { 0: maxLength }))
    .default(''),
});

export const WelcomeTriggerCard = ({ integration, canWrite }: Props) => {
  const { t } = useTranslation();
  const { updateIntegration, updateStatus } = useIntegrations<IntercomIntegration>(
    integration.desk_id,
    integration.integration_id
  );

  const form = useForm<WelcomeTriggerForm>({
    mode: 'onTouched',
    resolver: createYupResolver<WelcomeTriggerForm>(schema),
    resetOptions: KEEP_DIRTY_VALUES,
    values: { welcome_trigger_message: integration.config?.welcome_trigger_message ?? '' },
  });

  const {
    control,
    getValues,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = form;

  useTrackFormState({ isDirty, formId: FORM_ID });

  const onSubmit = (values: WelcomeTriggerForm) => {
    const submitted = getValues('welcome_trigger_message');

    updateIntegration(
      { ...integration, config: { ...integration.config, ...values } },
      {
        onSuccess: () => {
          if (getValues('welcome_trigger_message') === submitted) {
            reset(values, CLEAR_DIRTY_VALUES);
          }
        },
      }
    );
  };

  return (
    <Form {...form}>
      <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
        <FieldsetCard
          isDirty={isDirty}
          isLoading={updateStatus === 'pending'}
          disabled={!isDirty || !canWrite}
          saveButtonLabel={t('common.save')}
          formId={FORM_ID}
        >
          <FormField
            control={control}
            name="welcome_trigger_message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('integrations.intercom.welcome_title')}</FormLabel>
                <FormControl>
                  <Input autoComplete="off" {...field} />
                </FormControl>
                <FormDescription>
                  {t('integrations.intercom.welcome_subtitle', {
                    0: integration.config?.admin_name,
                  })}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </FieldsetCard>
      </form>
    </Form>
  );
};
